/**
 * Repository Pattern Interfaces - Life-and-Death Grade Data Access
 *
 * These interfaces define the contract for all data access operations,
 * ensuring consistent error handling, tenant isolation, and audit logging
 * across all repositories in the system.
 */
import { success, failure } from '../types/Result';
import { RepositoryError } from '../errors/RepositoryError';
import { z } from 'zod';
/**
 * Entity metadata for audit and tenant isolation
 */
const EntityMetadataSchema = z.object({
    id: z.string().min(1),
    tenant: z.string().min(1),
    createdBy: z.string().min(1),
    createdAt: z.date(),
    updatedBy: z.string().optional(),
    updatedAt: z.date().optional(),
    version: z.number().int().min(1),
    tags: z.array(z.string()).default([]),
    metadata: z.record(z.unknown()).optional()
});
/**
 * Pagination parameters with validation
 */
const PaginationParamsSchema = z.object({
    offset: z.number().int().min(0).default(0),
    limit: z.number().int().min(1).max(1000).default(50),
    sortBy: z.string().optional(),
    sortOrder: z.enum(['ASC', 'DESC']).default('ASC'),
    cursor: z.string().optional() // For cursor-based pagination
});
/**
 * Abstract base repository with common functionality
 */
export class BaseRepository {
    constructor(entityName) {
        this.entityName = entityName;
    }
    // Common implementation for exists method
    async exists(id, context) {
        const result = await this.findById(id, context);
        return result.match({
            success: () => success(true),
            failure: (error) => {
                if (error.type === 'NOT_FOUND') {
                    return success(false);
                }
                return failure(error);
            }
        });
    }
    /**
     * Validate tenant context for operation
     */
    validateTenantContext(context) {
        if (!context.getCurrentTenant()) {
            return failure(RepositoryError.tenantViolation('required', 'none', {
                operation: 'validateTenantContext',
                component: this.entityName
            }));
        }
        return success(undefined);
    }
    /**
     * Validate operation context
     */
    validateOperationContext(context) {
        const tenantValidation = this.validateTenantContext(context.tenantContext);
        if (tenantValidation.isFailure()) {
            return tenantValidation;
        }
        if (!context.tenantContext.getCurrentUser()) {
            return failure(RepositoryError.unauthorized('write operation', {
                operation: 'validateOperationContext',
                component: this.entityName
            }));
        }
        return success(undefined);
    }
    /**
     * Create entity metadata
     */
    createEntityMetadata(context) {
        const now = new Date();
        const userId = context.tenantContext.getCurrentUser();
        return {
            id: '', // Will be set by concrete implementation
            tenant: context.tenantContext.getCurrentTenant(),
            createdBy: userId,
            createdAt: now,
            version: 1,
            tags: [],
            metadata: context.metadata
        };
    }
    /**
     * Update entity metadata
     */
    updateEntityMetadata(current, context) {
        const userId = context.tenantContext.getCurrentUser();
        return {
            ...current,
            updatedBy: userId,
            updatedAt: new Date(),
            version: current.version + 1,
            metadata: {
                ...current.metadata,
                ...context.metadata
            }
        };
    }
}
/**
 * Utility functions for repository operations
 */
export class RepositoryUtils {
    /**
     * Validate pagination parameters
     */
    static validatePagination(params) {
        try {
            return success(PaginationParamsSchema.parse(params));
        }
        catch (error) {
            return failure(RepositoryError.validationFailed(String(error), {
                component: 'RepositoryUtils',
                operation: 'validatePagination'
            }));
        }
    }
    /**
     * Validate entity metadata
     */
    static validateEntityMetadata(metadata) {
        try {
            return success(EntityMetadataSchema.parse(metadata));
        }
        catch (error) {
            return failure(RepositoryError.validationFailed(String(error), {
                component: 'RepositoryUtils',
                operation: 'validateEntityMetadata'
            }));
        }
    }
    /**
     * Build cache key for entity
     */
    static buildCacheKey(entityName, id, tenant) {
        return `${entityName}:${tenant}:${id}`;
    }
    /**
     * Build query cache key
     */
    static buildQueryCacheKey(entityName, tenant, filters, pagination) {
        const filterHash = this.hashFilters(filters);
        const paginationHash = this.hashPagination(pagination);
        return `query:${entityName}:${tenant}:${filterHash}:${paginationHash}`;
    }
    static hashFilters(filters) {
        return btoa(JSON.stringify(filters.sort((a, b) => a.field.localeCompare(b.field))));
    }
    static hashPagination(pagination) {
        return btoa(JSON.stringify({
            offset: pagination.offset,
            limit: pagination.limit,
            sortBy: pagination.sortBy,
            sortOrder: pagination.sortOrder
        }));
    }
}
//# sourceMappingURL=Repository.js.map